import { ref, computed } from 'vue';
import { collection, getDocs, query, where, type Firestore } from 'firebase/firestore';
import type { MatchResult, PlayerGame } from './useMatchSearch';

// Type declaration for Firebase plugin
interface FirebaseInstance {
  app: any;
  auth: any;
  firestore: Firestore;
  storage: any;
}

/**
 * Head-to-head record against a specific opponent
 */
export interface HeadToHeadRecord {
  opponentId: string;
  opponentName: string;
  totalGames: number;
  wins: number;
  losses: number;
  winPercentage: number;
  matchesPlayed: number;
  lastMatchDate?: string;
  recentForm: ('W' | 'L')[]; // Last 5 games
}

/**
 * Comprehensive player data structure aggregating all player information
 */
export interface PlayerIndex {
  playerId: string;
  playerName: string;
  divisions: {
    divisionId: string;
    divisionName: string;
    teamName?: string;
    fargoRate?: number;
    gamesPlayed?: number;
    robustness?: number;
  }[];
  stats: {
    [divisionId: string]: {
      rank?: number;
      winPercentage: number;
      gamesWon: number;
      gamesLost: number;
      pointsForAgainst: string;
      weeks: number;
      breakAndRuns: number;
      winZips: number;
      tableRuns: number;
      winOnBreak: number;
      consecutiveWins: number;
      perfectMatches: number;
      teamName?: string; // For playerTeamStats
    };
  };
  matches: {
    matchId: string;
    divisionId: string;
    divisionName: string;
    matchDate: string;
    teamName: string; // Which team the player played for
    opponentTeamName: string;
    games: {
      gameNumber: number;
      roundNumber: number;
      opponentName: string;
      opponentId: string;
      won: boolean;
      score: string; // e.g., "5-3"
      chips: string[]; // Chips earned in this game
    }[];
    totalGames: number;
    gamesWon: number;
    gamesLost: number;
  }[];
  headToHead: {
    [opponentName: string]: HeadToHeadRecord;
  };
}

/**
 * Player search filters
 */
export interface PlayerSearchFilters {
  playerName?: string; // Partial name match (case-insensitive)
  divisionId?: string;
  teamName?: string;
  minFargoRate?: number;
  maxFargoRate?: number;
  minWinPercentage?: number;
  hasMatchHistory?: boolean; // Only players with match data
}

/**
 * Composable for indexing and searching player data across all sources
 */
export function usePlayerIndex(leagueId: string) {
  const playerIndex = ref<Map<string, PlayerIndex>>(new Map());
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastIndexedAt = ref<Date | null>(null);

  // Get Firebase lazily when needed
  function getDb() {
    const { $firebase } = useNuxtApp();
    const firebase = $firebase as FirebaseInstance;
    return firebase.firestore;
  }

  /**
   * Build a comprehensive player index from all data sources
   */
  async function buildPlayerIndex(divisionId?: string) {
    loading.value = true;
    error.value = null;
    const newIndex = new Map<string, PlayerIndex>();

    try {
      const db = getDb();
      console.log('🔨 Building player index for league:', leagueId, 'division:', divisionId || 'all');

      // 1. Index from playerList (base player info)
      await indexPlayerList(db, newIndex, divisionId);

      // 2. Index from playerStats (individual stats)
      await indexPlayerStats(db, newIndex, divisionId);

      // 3. Index from playerTeamStats (team-specific stats)
      await indexPlayerTeamStats(db, newIndex, divisionId);

      // 4. Index head-to-head records from pre-calculated collection
      await indexHeadToHead(db, newIndex);

      // 5. Index match history from pre-calculated collection
      await indexMatchHistory(db, newIndex);

      playerIndex.value = newIndex;
      lastIndexedAt.value = new Date();
      
      console.log('✅ Player index built successfully:', {
        totalPlayers: newIndex.size,
        playersWithStats: Array.from(newIndex.values()).filter(p => Object.keys(p.stats).length > 0).length,
        playersWithMatches: Array.from(newIndex.values()).filter(p => p.matches.length > 0).length,
        playersWithH2H: Array.from(newIndex.values()).filter(p => Object.keys(p.headToHead).length > 0).length,
        timestamp: lastIndexedAt.value
      });

    } catch (e: any) {
      error.value = e.message;
      console.error('❌ Error building player index:', e);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Index players from playerList collection
   */
  async function indexPlayerList(db: Firestore, index: Map<string, PlayerIndex>, divisionId?: string) {
    console.log('📋 Indexing from playerList...');
    const playerListRef = collection(db, `leagueStandings/${leagueId}/playerList`);
    const constraints = divisionId ? [where('divisionId', '==', divisionId)] : [];
    const q = query(playerListRef, ...constraints);
    const snapshot = await getDocs(q);

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const playerName = data.playerName;

      // Use playerName as ID since we don't have a unique playerId in playerList
      const playerId = playerName;

      if (!index.has(playerId)) {
        index.set(playerId, {
          playerId,
          playerName,
          divisions: [],
          stats: {},
          matches: [],
          headToHead: {}
        });
      }

      const player = index.get(playerId)!;
      
      // Add division info
      if (!player.divisions.find(d => d.divisionId === data.divisionId)) {
        player.divisions.push({
          divisionId: data.divisionId,
          divisionName: data.divisionName,
          teamName: data.teamName,
          fargoRate: data.fargoRate,
          gamesPlayed: data.gamesPlayed,
          robustness: data.robustness
        });
      }
    });

    console.log(`  ✓ Indexed ${snapshot.docs.length} player list entries`);
  }

  /**
   * Index players from playerStats collection
   */
  async function indexPlayerStats(db: Firestore, index: Map<string, PlayerIndex>, divisionId?: string) {
    console.log('📊 Indexing from playerStats...');
    const playerStatsRef = collection(db, `leagueStandings/${leagueId}/playerStats`);
    const constraints = divisionId ? [where('divisionId', '==', divisionId)] : [];
    const q = query(playerStatsRef, ...constraints);
    const snapshot = await getDocs(q);

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const playerName = data.playerName;
      const playerId = playerName;

      if (!index.has(playerId)) {
        index.set(playerId, {
          playerId,
          playerName,
          divisions: [],
          stats: {},
          matches: [],
          headToHead: {}
        });
      }

      const player = index.get(playerId)!;

      // Add division if not present
      if (!player.divisions.find(d => d.divisionId === data.divisionId)) {
        player.divisions.push({
          divisionId: data.divisionId,
          divisionName: data.divisionName
        });
      }

      // Add stats for this division
      player.stats[data.divisionId] = {
        rank: data.rank,
        winPercentage: data.winPercentage || 0,
        gamesWon: data.gamesWon || 0,
        gamesLost: data.gamesLost || 0,
        pointsForAgainst: data.pointsForAgainst || '0/0',
        weeks: data.weeks || 0,
        breakAndRuns: data.breakAndRuns || 0,
        winZips: data.winZips || 0,
        tableRuns: data.tableRuns || 0,
        winOnBreak: data.winOnBreak || 0,
        consecutiveWins: data.consecutiveWins || 0,
        perfectMatches: data.perfectMatches || 0
      };
    });

    console.log(`  ✓ Indexed ${snapshot.docs.length} player stats entries`);
  }

  /**
   * Index players from playerTeamStats collection
   */
  async function indexPlayerTeamStats(db: Firestore, index: Map<string, PlayerIndex>, divisionId?: string) {
    console.log('👥 Indexing from playerTeamStats...');
    const playerTeamStatsRef = collection(db, `leagueStandings/${leagueId}/playerTeamStats`);
    const constraints = divisionId ? [where('divisionId', '==', divisionId)] : [];
    const q = query(playerTeamStatsRef, ...constraints);
    const snapshot = await getDocs(q);

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const playerName = data.playerName;
      const playerId = playerName;

      if (!index.has(playerId)) {
        index.set(playerId, {
          playerId,
          playerName,
          divisions: [],
          stats: {},
          matches: [],
          headToHead: {}
        });
      }

      const player = index.get(playerId)!;

      // Update division info with team name
      const divisionIndex = player.divisions.findIndex(d => d.divisionId === data.divisionId);
      if (divisionIndex >= 0) {
        player.divisions[divisionIndex].teamName = data.teamName;
      } else {
        player.divisions.push({
          divisionId: data.divisionId,
          divisionName: data.divisionName,
          teamName: data.teamName
        });
      }

      // Update stats with team name (prefer playerTeamStats over playerStats)
      if (player.stats[data.divisionId]) {
        player.stats[data.divisionId].teamName = data.teamName;
      } else {
        player.stats[data.divisionId] = {
          winPercentage: data.winPercentage || 0,
          gamesWon: data.gamesWon || 0,
          gamesLost: data.gamesLost || 0,
          pointsForAgainst: data.pointsForAgainst || '0/0',
          weeks: data.weeks || 0,
          breakAndRuns: data.breakAndRuns || 0,
          winZips: data.winZips || 0,
          tableRuns: data.tableRuns || 0,
          winOnBreak: data.winOnBreak || 0,
          consecutiveWins: data.consecutiveWins || 0,
          perfectMatches: data.perfectMatches || 0,
          teamName: data.teamName
        };
      }
    });

    console.log(`  ✓ Indexed ${snapshot.docs.length} player team stats entries`);
  }

  /**
   * Index head-to-head records from pre-calculated collection
   */
  async function indexHeadToHead(db: Firestore, index: Map<string, PlayerIndex>) {
    console.log('🥊 Indexing head-to-head records...');
    const h2hRef = collection(db, `leagueStandings/${leagueId}/playerHeadToHead`);
    const snapshot = await getDocs(h2hRef);

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const playerId = data.playerId;

      if (!index.has(playerId)) {
        index.set(playerId, {
          playerId,
          playerName: playerId,
          divisions: [],
          stats: {},
          matches: [],
          headToHead: {}
        });
      }

      const player = index.get(playerId)!;
      player.headToHead = data.headToHead || {};
    });

    console.log(`  ✓ Indexed ${snapshot.docs.length} head-to-head records`);
  }

  /**
   * Index match history from pre-calculated collection
   */
  async function indexMatchHistory(db: Firestore, index: Map<string, PlayerIndex>) {
    console.log('📜 Indexing match histories...');
    const historyRef = collection(db, `leagueStandings/${leagueId}/playerMatchHistory`);
    const snapshot = await getDocs(query(historyRef));

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const playerId = data.playerId;

      if (!index.has(playerId)) {
        index.set(playerId, {
          playerId,
          playerName: playerId,
          divisions: [],
          stats: {},
          matches: [],
          headToHead: {}
        });
      }

      const player = index.get(playerId)!;
      player.matches = data.matches || [];

      // Ensure divisions from matches are tracked
      data.matches?.forEach((match: any) => {
        if (!player.divisions.find(d => d.divisionId === match.divisionId)) {
          player.divisions.push({
            divisionId: match.divisionId,
            divisionName: match.divisionName
          });
        }
      });
    });

    console.log(`  ✓ Indexed ${snapshot.docs.length} match histories`);
  }

  /**
   * Search for players using filters
   */
  function searchPlayers(filters: PlayerSearchFilters = {}): PlayerIndex[] {
    let results = Array.from(playerIndex.value.values());

    // Filter by player name (partial match, case-insensitive)
    if (filters.playerName) {
      const searchTerm = filters.playerName.toLowerCase();
      results = results.filter(player => 
        player.playerName.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by division
    if (filters.divisionId) {
      results = results.filter(player =>
        player.divisions.some(d => d.divisionId === filters.divisionId)
      );
    }

    // Filter by team name
    if (filters.teamName) {
      results = results.filter(player =>
        player.divisions.some(d => d.teamName === filters.teamName)
      );
    }

    // Filter by Fargo rate range
    if (filters.minFargoRate !== undefined || filters.maxFargoRate !== undefined) {
      results = results.filter(player => {
        const fargoRates = player.divisions
          .map(d => d.fargoRate)
          .filter(rate => rate !== undefined) as number[];
        
        if (fargoRates.length === 0) return false;
        
        const maxFargo = Math.max(...fargoRates);
        
        if (filters.minFargoRate !== undefined && maxFargo < filters.minFargoRate) return false;
        if (filters.maxFargoRate !== undefined && maxFargo > filters.maxFargoRate) return false;
        
        return true;
      });
    }

    // Filter by win percentage
    if (filters.minWinPercentage !== undefined) {
      results = results.filter(player => {
        const winPercentages = Object.values(player.stats).map(s => s.winPercentage);
        if (winPercentages.length === 0) return false;
        const maxWinPct = Math.max(...winPercentages);
        return maxWinPct >= filters.minWinPercentage!;
      });
    }

    // Filter by match history
    if (filters.hasMatchHistory) {
      results = results.filter(player => player.matches.length > 0);
    }

    // Sort by player name
    results.sort((a, b) => a.playerName.localeCompare(b.playerName));

    return results;
  }

  /**
   * Get a single player's full data
   */
  function getPlayer(playerName: string): PlayerIndex | undefined {
    return playerIndex.value.get(playerName);
  }

  /**
   * Get all unique player names (for autocomplete)
   */
  const allPlayerNames = computed(() => {
    return Array.from(playerIndex.value.keys()).sort();
  });

  /**
   * Get players by division
   */
  function getPlayersByDivision(divisionId: string): PlayerIndex[] {
    return Array.from(playerIndex.value.values())
      .filter(player => player.divisions.some(d => d.divisionId === divisionId))
      .sort((a, b) => a.playerName.localeCompare(b.playerName));
  }

  /**
   * Get players by team
   */
  function getPlayersByTeam(teamName: string, divisionId?: string): PlayerIndex[] {
    return Array.from(playerIndex.value.values())
      .filter(player => {
        const matchingDivisions = divisionId
          ? player.divisions.filter(d => d.divisionId === divisionId && d.teamName === teamName)
          : player.divisions.filter(d => d.teamName === teamName);
        return matchingDivisions.length > 0;
      })
      .sort((a, b) => a.playerName.localeCompare(b.playerName));
  }

  /**
   * Get match history for a specific player
   */
  function getPlayerMatchHistory(playerName: string, divisionId?: string) {
    const player = playerIndex.value.get(playerName);
    if (!player) return [];

    let matches = player.matches;
    
    if (divisionId) {
      matches = matches.filter(m => m.divisionId === divisionId);
    }

    // Sort by date (most recent first)
    return matches.sort((a, b) => 
      new Date(b.matchDate).getTime() - new Date(a.matchDate).getTime()
    );
  }

  /**
   * Get player statistics summary
   */
  function getPlayerStatsSummary(playerName: string) {
    const player = playerIndex.value.get(playerName);
    if (!player) return null;

    // Aggregate stats across all divisions
    const allStats = Object.values(player.stats);
    
    const totalGamesWon = allStats.reduce((sum, s) => sum + s.gamesWon, 0);
    const avgWinPercentage = allStats.length > 0
      ? allStats.reduce((sum, s) => sum + s.winPercentage, 0) / allStats.length
      : 0;
    
    const totalBreakAndRuns = allStats.reduce((sum, s) => sum + s.breakAndRuns, 0);
    const totalWinZips = allStats.reduce((sum, s) => sum + s.winZips, 0);
    const totalTableRuns = allStats.reduce((sum, s) => sum + s.tableRuns, 0);

    return {
      playerName: player.playerName,
      divisions: player.divisions.length,
      totalGamesWon,
      avgWinPercentage: Math.round(avgWinPercentage * 100) / 100,
      totalMatches: player.matches.length,
      totalBreakAndRuns,
      totalWinZips,
      totalTableRuns,
      fargoRate: player.divisions[0]?.fargoRate
    };
  }

  /**
   * Get head-to-head record against a specific opponent
   */
  function getHeadToHead(playerName: string, opponentName: string): HeadToHeadRecord | null {
    const player = playerIndex.value.get(playerName);
    if (!player) return null;
    
    return player.headToHead[opponentName] || null;
  }

  /**
   * Get all head-to-head records for a player, sorted by total games played
   */
  function getAllHeadToHeadRecords(playerName: string): HeadToHeadRecord[] {
    const player = playerIndex.value.get(playerName);
    if (!player) return [];
    
    return Object.values(player.headToHead).sort((a, b) => b.totalGames - a.totalGames);
  }

  /**
   * Get top opponents (by games played)
   */
  function getTopOpponents(playerName: string, limit = 5): HeadToHeadRecord[] {
    return getAllHeadToHeadRecords(playerName).slice(0, limit);
  }

  return {
    // State
    playerIndex,
    loading,
    error,
    lastIndexedAt,
    allPlayerNames,

    // Methods
    buildPlayerIndex,
    searchPlayers,
    getPlayer,
    getPlayersByDivision,
    getPlayersByTeam,
    getPlayerMatchHistory,
    getPlayerStatsSummary,
    getHeadToHead,
    getAllHeadToHeadRecords,
    getTopOpponents
  };
}
